import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { 
  LayoutDashboard, Users, Calendar, CreditCard, Bell, LogOut, 
  Menu, X, FileText, Edit3, Save, Upload, Image, Trash2, 
  RefreshCw, Eye, EyeOff, GripVertical, Plus, Check, Languages
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AdminSidebar from "@/components/AdminSidebar";
import { useNotificationCount } from "@/hooks/useNotificationCount";

const API = "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a07fe825-978d-42af-ad0d-66b05bf66e9f/artifacts/6h7nm3ap_Photoroom_20260215_225825.png";

const ContentManager = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const notificationCount = useNotificationCount();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState({});
  const [defaults, setDefaults] = useState({});
  const [editedContent, setEditedContent] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState("landing");
  const [images, setImages] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [pendingQuotes, setPendingQuotes] = useState(0);


  useEffect(() => {
    fetchContent();
    fetchImages();
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const res = await axios.get(`${API}/quotes`, { withCredentials: true });
      setPendingQuotes(res.data.filter(q => q.status === "pending").length);
    } catch (error) {
      console.error("Failed to fetch quotes:", error);
    }
  };

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/cms/content`, { withCredentials: true });
      setContent(response.data.content || {});
      setDefaults(response.data.defaults || {});
      // Initialize edited content with merged defaults and saved content
      const merged = mergeContentWithDefaults(response.data.content || {}, response.data.defaults || {});
      setEditedContent(merged);
    } catch (error) {
      console.error("Failed to fetch content:", error);
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/cms/images`, { withCredentials: true });
      setImages(response.data);
    } catch (error) {
      console.error("Failed to fetch images:", error);
    }
  };

  const mergeContentWithDefaults = (saved, defaults) => {
    const merged = {};
    // Start with defaults
    Object.keys(defaults).forEach(page => {
      merged[page] = {};
      Object.keys(defaults[page]).forEach(section => {
        merged[page][section] = { ...defaults[page][section] };
      });
    });
    // Override with saved content
    Object.keys(saved).forEach(page => {
      if (!merged[page]) merged[page] = {};
      Object.keys(saved[page]).forEach(section => {
        if (!merged[page][section]) merged[page][section] = {};
        Object.keys(saved[page][section]).forEach(key => {
          merged[page][section][key] = saved[page][section][key];
        });
      });
    });
    return merged;
  };

  const handleContentChange = (page, section, key, value) => {
    setEditedContent(prev => ({
      ...prev,
      [page]: {
        ...prev[page],
        [section]: {
          ...prev[page]?.[section],
          [key]: value
        }
      }
    }));
    setHasChanges(true);
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      const updates = [];
      // Flatten edited content for bulk update
      Object.keys(editedContent).forEach(page => {
        Object.keys(editedContent[page] || {}).forEach(section => {
          Object.keys(editedContent[page][section] || {}).forEach(key => {
            updates.push({
              page,
              section,
              content_key: key,
              value: editedContent[page][section][key],
              content_type: "text"
            });
          });
        });
      });

      await axios.put(`${API}/cms/content/bulk`, { updates }, { withCredentials: true });
      toast.success("Content saved successfully!");
      setHasChanges(false);
      fetchContent();
    } catch (error) {
      console.error("Failed to save content:", error);
      toast.error("Failed to save content");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async (page, section = null) => {
    if (!window.confirm(`Are you sure you want to reset ${section ? `${section} section` : `${page} page`} to defaults?`)) {
      return;
    }
    try {
      await axios.post(`${API}/cms/reset-defaults`, { page, section }, { withCredentials: true });
      toast.success("Reset to defaults");
      fetchContent();
    } catch (error) {
      toast.error("Failed to reset");
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/cms/upload-image`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Image uploaded!");
      fetchImages();
      setUploadDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      handleImageUpload(file);
    } else {
      toast.error("Please drop an image file");
    }
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await axios.delete(`${API}/cms/images/${imageId}`, { withCredentials: true });
      toast.success("Image deleted");
      fetchImages();
    } catch (error) {
      toast.error("Failed to delete image");
    }
  };

  const copyImageUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Image URL copied!");
  };

  const renderContentSection = (page, sectionKey, sectionData) => {
    return (
      <div key={sectionKey} className="bg-[#121212] border border-[#27272A] rounded-xl p-6 mb-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold text-white uppercase flex items-center gap-2">
            <Edit3 className="w-5 h-5 text-[#39FF14]" />
            {sectionKey.replace(/_/g, " ")}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => resetToDefaults(page, sectionKey)}
            className="text-[#A1A1AA] hover:text-red-400"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Reset
          </Button>
        </div>
        <div className="space-y-4">
          {Object.keys(sectionData).map(key => (
            <div key={key}>
              <Label className="text-[#A1A1AA] text-sm capitalize">
                {key.replace(/_/g, " ")}
              </Label>
              {sectionData[key]?.length > 100 ? (
                <Textarea
                  value={editedContent[page]?.[sectionKey]?.[key] || ""}
                  onChange={(e) => handleContentChange(page, sectionKey, key, e.target.value)}
                  className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14] mt-1"
                  rows={3}
                />
              ) : (
                <Input
                  value={editedContent[page]?.[sectionKey]?.[key] || ""}
                  onChange={(e) => handleContentChange(page, sectionKey, key, e.target.value)}
                  className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14] mt-1"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <AdminSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} notificationCount={notificationCount} />

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#0A0A0A]/80 backdrop-blur-lg border-b border-[#27272A] p-4 lg:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                className="lg:hidden text-[#A1A1AA] hover:text-white"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-heading text-2xl lg:text-3xl font-bold text-white uppercase">Content Manager</h1>
                <p className="text-[#A1A1AA] text-sm">Edit text and manage images across all pages</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setUploadDialogOpen(true)}
                variant="outline"
                className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A] hover:text-white"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Image
              </Button>
              {hasChanges && (
                <Button
                  onClick={saveContent}
                  disabled={saving}
                  className="bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              )}
            </div>
          </div>
        </header>

        <div className="p-4 lg:p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-[#39FF14] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="bg-[#121212] border border-[#27272A] mb-6">
                <TabsTrigger 
                  value="landing" 
                  className="data-[state=active]:bg-[#39FF14]/20 data-[state=active]:text-[#39FF14]"
                >
                  Landing Page
                </TabsTrigger>
                <TabsTrigger 
                  value="portal" 
                  className="data-[state=active]:bg-[#39FF14]/20 data-[state=active]:text-[#39FF14]"
                >
                  Customer Portal
                </TabsTrigger>
                <TabsTrigger 
                  value="dashboard" 
                  className="data-[state=active]:bg-[#39FF14]/20 data-[state=active]:text-[#39FF14]"
                >
                  Dashboard
                </TabsTrigger>
                <TabsTrigger 
                  value="images" 
                  className="data-[state=active]:bg-[#39FF14]/20 data-[state=active]:text-[#39FF14]"
                >
                  <Image className="w-4 h-4 mr-2" />
                  Images
                </TabsTrigger>
                <TabsTrigger 
                  value="translations" 
                  className="data-[state=active]:bg-[#39FF14]/20 data-[state=active]:text-[#39FF14]"
                >
                  <Languages className="w-4 h-4 mr-2" />
                  Translations
                </TabsTrigger>
              </TabsList>

              {/* Landing Page Content */}
              <TabsContent value="landing">
                <div className="grid gap-4">
                  {defaults.landing && Object.keys(defaults.landing).map(sectionKey => 
                    renderContentSection("landing", sectionKey, defaults.landing[sectionKey])
                  )}
                </div>
              </TabsContent>

              {/* Customer Portal Content */}
              <TabsContent value="portal">
                <div className="grid gap-4">
                  {defaults.portal && Object.keys(defaults.portal).map(sectionKey => 
                    renderContentSection("portal", sectionKey, defaults.portal[sectionKey])
                  )}
                </div>
              </TabsContent>

              {/* Dashboard Content */}
              <TabsContent value="dashboard">
                <div className="grid gap-4">
                  {defaults.dashboard && Object.keys(defaults.dashboard).map(sectionKey => 
                    renderContentSection("dashboard", sectionKey, defaults.dashboard[sectionKey])
                  )}
                </div>
              </TabsContent>

              {/* Images Tab */}
              <TabsContent value="images">
                <div 
                  className={`border-2 border-dashed rounded-xl p-8 mb-6 transition-all ${
                    dragOver 
                      ? "border-[#39FF14] bg-[#39FF14]/10" 
                      : "border-[#27272A] bg-[#121212]"
                  }`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <div className="text-center">
                    <Upload className={`w-12 h-12 mx-auto mb-4 ${dragOver ? "text-[#39FF14]" : "text-[#A1A1AA]"}`} />
                    <p className="text-white font-medium mb-2">Drag & drop images here</p>
                    <p className="text-[#A1A1AA] text-sm mb-4">or click the button below to select files</p>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files[0])}
                      className="hidden"
                      id="image-upload"
                    />
                    <label htmlFor="image-upload">
                      <Button asChild className="bg-[#39FF14] text-black hover:bg-[#32D711] cursor-pointer">
                        <span>
                          <Plus className="w-4 h-4 mr-2" />
                          Select Image
                        </span>
                      </Button>
                    </label>
                  </div>
                </div>

                {images.length === 0 ? (
                  <div className="text-center py-12">
                    <Image className="w-16 h-16 text-[#27272A] mx-auto mb-4" />
                    <p className="text-[#A1A1AA]">No images uploaded yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {images.map((image) => (
                      <div 
                        key={image.image_id}
                        className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden group"
                      >
                        <div className="aspect-square relative">
                          <img 
                            src={image.url} 
                            alt={image.filename}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => copyImageUrl(image.url)}
                              className="bg-[#39FF14] text-black hover:bg-[#32D711]"
                            >
                              Copy URL
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteImage(image.image_id)}
                              className="border-red-400 text-red-400 hover:bg-red-400/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="p-3">
                          <p className="text-white text-sm truncate">{image.filename}</p>
                          <p className="text-[#A1A1AA] text-xs">{Math.round(image.size / 1024)} KB</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Translations Tab */}
              <TabsContent value="translations">
                <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <Languages className="w-8 h-8 text-[#39FF14]" />
                      <div>
                        <h2 className="font-heading text-xl font-bold text-white uppercase">Multi-Language Support</h2>
                        <p className="text-[#A1A1AA] text-sm">Edit text in English and Japanese</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-[#0A0A0A] rounded-lg p-6 mb-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#39FF14]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Languages className="w-6 h-6 text-[#39FF14]" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-2">Translation Manager</h3>
                        <p className="text-[#A1A1AA] text-sm mb-4">
                          Manage all translations for your website. Edit text in both English and Japanese, 
                          organized by category (Landing Page, Booking, Portal, Admin, etc.).
                        </p>
                        <ul className="text-[#A1A1AA] text-sm space-y-1 mb-4">
                          <li>• 150+ pre-translated phrases</li>
                          <li>• Organized by category for easy editing</li>
                          <li>• Search and filter translations</li>
                          <li>• Visitors can switch languages with the floating button</li>
                        </ul>
                        <Button
                          onClick={() => navigate('/translations')}
                          className="bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                        >
                          <Languages className="w-4 h-4 mr-2" />
                          Open Translation Manager
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-[#0A0A0A] rounded-lg p-4 border border-[#27272A]">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-8 h-5 bg-blue-600 rounded text-xs flex items-center justify-center font-bold text-white">EN</span>
                        <span className="text-white font-medium">English</span>
                      </div>
                      <p className="text-[#A1A1AA] text-sm">Default language for all content</p>
                    </div>
                    <div className="bg-[#0A0A0A] rounded-lg p-4 border border-[#27272A]">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-8 h-5 bg-red-600 rounded text-xs flex items-center justify-center font-bold text-white">JA</span>
                        <span className="text-white font-medium">日本語 (Japanese)</span>
                      </div>
                      <p className="text-[#A1A1AA] text-sm">Secondary language with full translation support</p>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </main>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Upload Image</DialogTitle>
          </DialogHeader>
          <div 
            className={`border-2 border-dashed rounded-xl p-8 transition-all ${
              dragOver ? "border-[#39FF14] bg-[#39FF14]/10" : "border-[#27272A]"
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <div className="text-center">
              <Upload className={`w-12 h-12 mx-auto mb-4 ${dragOver ? "text-[#39FF14]" : "text-[#A1A1AA]"}`} />
              <p className="text-white font-medium mb-2">Drag & drop an image</p>
              <p className="text-[#A1A1AA] text-sm mb-4">JPEG, PNG, GIF, or WEBP (max 5MB)</p>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e.target.files[0])}
                className="hidden"
                id="dialog-image-upload"
              />
              <label htmlFor="dialog-image-upload">
                <Button asChild disabled={uploading} className="bg-[#39FF14] text-black hover:bg-[#32D711] cursor-pointer">
                  <span>
                    {uploading ? (
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                    ) : (
                      <Plus className="w-4 h-4 mr-2" />
                    )}
                    Select File
                  </span>
                </Button>
              </label>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentManager;
